import React, {useContext, useEffect, useState} from "react";
import {AiPromptServiceContext, AiProviderConnectionServiceContext, AiReportSummarizerServiceContext, AppContextContext, OrganizationServiceContext} from "../../Contexts";
import {AutoComplete, Breadcrumb, Button, Descriptions, Form, Input, message, Modal, Select, Spin, Switch, Tag} from "antd";
import {LoadingOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import * as globalStyles from "../App.module.css";
import * as styles from "./AiReportSummarizerDetail.module.css";
import {Link, useNavigate, useParams} from "react-router-dom";
import {DocumentTitle} from "../DocumentTitle";
import AiReportSummarizer from "../../domain/AiReportSummarizer";
import {useForm, useWatch} from "antd/es/form/Form";
import {AiExtractorType, AiExtractorWebSearchMode, ExternalWebSearchBackend, formatAiExtractorType, formatAiExtractorWebSearchMode, formatExternalWebSearchBackend, OpenAiReasoningEffort, SupportedModel} from "../../domain/AiExtractor";
import {AiExtractorWebSearchFields, buildValuesForTypeChange, getSupportedModelsForType, isWebSearchSupportedForModel} from "../aiextractor/AiExtractorWebSearch";
import QueryOptions from "../../sal-ui/QueryOptions";
import AiPrompt from "../../domain/AiPrompt";
import AiProviderConnection from "../../domain/AiProviderConnection";
import Organization from "../../domain/Organization";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import {renderOrganizationLink} from "../common/OrganizationLink";
import {Permission} from "../../domain/auth/Permission";
import {buildAiPromptSelectOptions} from "../aiprompt/AiPromptSelectOptions";

type SupportedModelsByType = Record<string, { models: SupportedModel[] }>;

function AiReportSummarizerDetail() {
    const appContext = useContext(AppContextContext);
    const aiReportSummarizerService = useContext(AiReportSummarizerServiceContext);
    const aiPromptService = useContext(AiPromptServiceContext);
    const aiProviderConnectionService = useContext(AiProviderConnectionServiceContext);
    const organizationService = useContext(OrganizationServiceContext);
    const navigate = useNavigate();
    const {aiReportSummarizerId, cloneSummarizerId} = useParams<{aiReportSummarizerId: string; cloneSummarizerId: string}>();
    const [summarizer, setSummarizer] = useState<AiReportSummarizer | undefined>();
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(() => !!aiReportSummarizerId || !!cloneSummarizerId);
    const [saving, setSaving] = useState(false);
    const [allSupportedModels, setAllSupportedModels] = useState<SupportedModelsByType>({});
    const [availableAiPrompts, setAvailableAiPrompts] = useState<AiPrompt[]>([]);
    const [availableAiProviderConnections, setAvailableAiProviderConnections] = useState<AiProviderConnection[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [form] = useForm();

    const isSystemAdmin = appContext.user.isSystemAdmin();
    const userOrganizationId = appContext.user.organization?.id;
    const sourceId = aiReportSummarizerId ?? cloneSummarizerId;
    const hasManage = isSystemAdmin || appContext.user.hasPermission(Permission.AI_REPORT_SUMMARIZER__MANAGE_ORG);
    const type = useWatch("type", form);
    const owner = useWatch("owner", form);
    const organizationId = useWatch("organization", form);
    const selectedModel = useWatch("model", form);
    const selectedWebSearchMode = useWatch("webSearchMode", form);
    const supportedModels = getSupportedModelsForType(allSupportedModels, type);
    const selectedSupportedModel = supportedModels.find(model => model.name === selectedModel);
    const webSearchSupportedBySelectedModel = isWebSearchSupportedForModel(type, selectedModel, selectedSupportedModel);
    const title = cloneSummarizerId ? `Clone of ${summarizer?.name ?? "AI report summarizer"}`
        : aiReportSummarizerId ? (summarizer?.name || "AI report summarizer") : "New AI report summarizer";
    const ownershipEditable = !aiReportSummarizerId;
    const allowedTypes = Object.keys(allSupportedModels) as AiExtractorType[];
    const canDirectlyManageSummarizer = hasManage
        && (!summarizer || isSystemAdmin || summarizer.owner === ResourceOwnerType.ORGANIZATION);
    const canReadAiPrompts = appContext.user.hasOneOfPermission([
        Permission.AI_PROMPT__READ_SEAL,
        Permission.AI_PROMPT__READ_ORG,
        Permission.AI_PROMPT__MANAGE_ORG
    ]);
    const showEditForm = (!sourceId || !!summarizer)
        && ((!aiReportSummarizerId && hasManage) || (editMode && canDirectlyManageSummarizer));

    const layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 18}
    };

    const tailLayout = {
        wrapperCol: {offset: 6, span: 18}
    };

    useEffect(() => {
        aiReportSummarizerService.getSupportedModels().then(setAllSupportedModels);

        if (canReadAiPrompts) {
            aiPromptService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
                .then(result => setAvailableAiPrompts(result.data));
        } else {
            setAvailableAiPrompts([]);
        }

        if (isSystemAdmin) {
            aiProviderConnectionService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
                .then(result => setAvailableAiProviderConnections(result.data));
            organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
                .then(result => setOrganizations(result.data));
        }
    }, [aiReportSummarizerService, aiPromptService, aiProviderConnectionService, organizationService, canReadAiPrompts, isSystemAdmin]);

    useEffect(() => {
        let active = true;
        const defaults = {
            owner: isSystemAdmin ? ResourceOwnerType.SYSTEM : ResourceOwnerType.ORGANIZATION,
            organization: isSystemAdmin ? undefined : userOrganizationId,
            webSearchMode: AiExtractorWebSearchMode.DISABLED,
            externalWebSearchBackend: ExternalWebSearchBackend.BRAVE
        };

        setSummarizer(undefined);
        setEditMode(false);
        setLoading(!!sourceId);

        form.resetFields();
        form.setFieldsValue(defaults);

        if (sourceId) {
            aiReportSummarizerService.get(sourceId).then(value => {
                if (!active) {
                    return;
                }

                setSummarizer(value);

                form.setFieldsValue({
                    ...toFormValues(value),
                    name: cloneSummarizerId ? `Clone of ${value.name}`.slice(0, 100) : value.name,
                    owner: cloneSummarizerId && !isSystemAdmin ? defaults.owner : value.owner,
                    organization: cloneSummarizerId && !isSystemAdmin ? defaults.organization : value.organization?.id
                });
            }).finally(() => {
                if (active) {
                    setLoading(false);
                }
            });
        }

        return () => {
            active = false;
        };
    }, [sourceId, cloneSummarizerId, aiReportSummarizerService, form, isSystemAdmin, userOrganizationId]);

    useEffect(() => {
        if (type === AiExtractorType.OLLAMA && !selectedModel && supportedModels.length > 0) {
            form.setFieldValue("model", supportedModels[0].name);
        }
    }, [form, selectedModel, supportedModels, type]);

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title: <Link to={"/definitions/ai-report-summarizers"}>AI report summarizers</Link>},
                                {title}
                            ]}
                />

                <Spin
                    spinning={loading}
                    indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}
                >
                    <h1>
                        {title}
                        {aiReportSummarizerId && summarizer?.defaultSummarizer && <Tag color="blue" style={{marginLeft: 8}}>default</Tag>}
                    </h1>

                    {aiReportSummarizerId && summarizer && hasManage &&
                        <div className={globalStyles["common__top-button-bar"]}>
                            {canDirectlyManageSummarizer && <>
                                <Button disabled={saving} onClick={() => setEditMode(!editMode)}>
                                    Edit
                                </Button>
                                <Button
                                    danger
                                    disabled={saving || summarizer.inUse}
                                    title={summarizer?.inUse ? "This summarizer is in use and cannot be deleted" : "Delete"}
                                    style={{marginLeft: 8}}
                                    onClick={() => {
                                        Modal.confirm({
                                            content: "Do you really want to delete this AI report summarizer?",
                                            okText: "Delete",
                                            cancelText: "Cancel",
                                            okButtonProps: {danger: true},
                                            onOk: () => onDelete()
                                        });
                                    }}>
                                    Delete
                                </Button>
                            </>}
                            <Button disabled={saving} onClick={() => navigate(`/definitions/ai-report-summarizers/clone/${aiReportSummarizerId}`)}>Clone</Button>
                        </div>
                    }

                    <Collapse isOpen={showEditForm}>
                        {aiReportSummarizerId && <h3>Edit AI report summarizer</h3>}
                        {renderEditForm()}
                    </Collapse>

                    {aiReportSummarizerId && summarizer && renderDetails()}
                </Spin>
            </>
        </DocumentTitle>
    );

    function renderEditForm() {
        return (
            <Form
                {...layout}
                form={form}
                onFinish={onFinish}
                disabled={saving || loading}
                className={`${globalStyles["common__form"]} ${styles["edit-form"]} ${aiReportSummarizerId ? globalStyles["common_form--edit"] : globalStyles["common_form--add"]}`}
            >
                {renderBasicFields()}
                {isSystemAdmin && renderOwnershipFields()}
                {renderTypeField()}
                {type && renderModelConfigurationFields()}
                {type && renderPromptFields()}
                {renderFormButtons()}
            </Form>
        );
    }

    function renderBasicFields() {
        return <>
            <Form.Item name="name" label="Name" rules={[{required: true, message: "Name is required."}]}>
                <Input maxLength={100}/>
            </Form.Item>

            <Form.Item name="description" label="Description">
                <Input.TextArea rows={3} maxLength={1000}/>
            </Form.Item>
        </>;
    }

    function renderOwnershipFields() {
        return <>
            <Form.Item
                name="owner"
                label="Owner"
                rules={[{required: true, message: "Owner is required."}]}
            >
                <Select
                    disabled={saving || !ownershipEditable}
                    options={[{
                        value: ResourceOwnerType.SYSTEM,
                        label: "system"
                    }, {
                        value: ResourceOwnerType.ORGANIZATION,
                        label: "organization"
                    }]}
                />
            </Form.Item>

            {owner === ResourceOwnerType.ORGANIZATION &&
                <Form.Item
                    name="organization"
                    label="Organization"
                    rules={[{required: true, message: "Organization is required."}]}
                >
                    <Select
                        showSearch
                        disabled={saving || !ownershipEditable}
                        optionFilterProp="label"
                        options={organizations.map(organization => ({
                            value: organization.id!,
                            label: organization.name || organization.id
                        }))}
                    />
                </Form.Item>
            }
        </>;
    }

    function renderTypeField() {
        return (
            <Form.Item name="type" label="Type" rules={[
                {required: true, message: "Type is required."},
                ...(cloneSummarizerId && !isSystemAdmin ? [{
                    type: "enum" as const,
                    enum: allowedTypes,
                    message: "Select a type available to your organization."
                }] : [])
            ]}>
                <Select
                    onChange={() => form.setFieldsValue(buildValuesForTypeChange())}
                >
                    {allowedTypes.map(type => (
                        <Select.Option key={type} value={type}>
                            {formatAiExtractorType(type)}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
        );
    }

    function renderModelConfigurationFields() {
        return <>
            {renderModelField()}
            {isSystemAdmin && renderProviderConnectionField()}
            {isSystemAdmin && renderOpenAiConfigurationFields()}
            {isSystemAdmin && renderProviderReasoningEffortFields()}
            {renderWebSearchFields()}
            {isSystemAdmin && (
                <Form.Item
                    name="aiProviderCommunicationLoggingEnabled"
                    label="Log AI provider calls"
                    valuePropName="checked"
                >
                    <Switch/>
                </Form.Item>
            )}
        </>;
    }

    function renderModelField() {
        return (
            <Form.Item
                name="model"
                label="Model"
                rules={[
                    {required: true, message: "Model is required."},
                    ...(cloneSummarizerId && !isSystemAdmin ? [{
                        type: "enum" as const,
                        enum: supportedModels.map(model => model.name),
                        message: "Select a model available to your organization."
                    }] : [])
                ]}
            >
                {type === AiExtractorType.OLLAMA || type === AiExtractorType.OPENAI_COMPATIBLE_API ? (
                    <AutoComplete
                        options={supportedModels.map(model => ({label: model.name, value: model.name}))}
                        filterOption={(inputValue, option) => {
                            if (!inputValue) {
                                return true;
                            }

                            return String(option?.value || "").toLowerCase().includes(inputValue.toLowerCase());
                        }}
                        placeholder={"Select or type model"}
                    >
                        <Input allowClear maxLength={100}/>
                    </AutoComplete>
                ) : (
                    <Select
                        showSearch
                        optionFilterProp="label"
                        options={supportedModels.map(model => ({label: model.name, value: model.name}))}
                        placeholder={"Select model"}
                    />
                )}
            </Form.Item>
        );
    }

    function renderProviderConnectionField() {
        if (type !== AiExtractorType.OPENAI_COMPATIBLE_API) {
            return null;
        }

        return (
            <Form.Item
                name="aiProviderConnectionId"
                label="Provider connection"
                rules={[{required: true, message: "Provider connection is required."}]}
            >
                <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder={"Select provider connection"}
                    options={availableAiProviderConnections.map(connection => ({
                        value: connection.id!,
                        label: connection.name || connection.id
                    }))}
                />
            </Form.Item>
        );
    }

    function renderOpenAiConfigurationFields() {
        if (type !== AiExtractorType.OPENAI_API && type !== AiExtractorType.OPENAI_COMPATIBLE_API) {
            return null;
        }

        return <>
            {type === AiExtractorType.OPENAI_API &&
                <Form.Item name="openAiServiceTier" label="Service tier">
                    <Select
                        allowClear
                        options={buildOptionListWithDefault(selectedSupportedModel?.supportedOpenAiServiceTiers || [])}
                        placeholder={"Select service tier"}
                    />
                </Form.Item>
            }

            <Form.Item
                name="openAiReasoningEffort"
                label="Reasoning effort"
                rules={type === AiExtractorType.OPENAI_API
                    ? [{required: true, message: "Reasoning effort is required."}]
                    : []}
            >
                <Select
                    allowClear
                    options={type === AiExtractorType.OPENAI_API
                        ? buildOptionListWithDefault(
                            selectedSupportedModel?.supportedOpenAiReasoningEfforts || [],
                            selectedSupportedModel?.defaultOpenAiReasoningEffort
                        )
                        : buildOptionListWithDefault(Object.values(OpenAiReasoningEffort))}
                    placeholder={"Select reasoning effort"}
                />
            </Form.Item>
        </>;
    }

    function renderProviderReasoningEffortFields() {
        if (type === AiExtractorType.ANTHROPIC_API && (selectedSupportedModel?.supportedAnthropicReasoningEfforts?.length ?? 0) > 0) {
            return (
                <Form.Item name="anthropicReasoningEffort" label="Reasoning effort">
                    <Select
                        allowClear
                        options={buildOptionListWithDefault(
                            selectedSupportedModel?.supportedAnthropicReasoningEfforts || [],
                            selectedSupportedModel?.defaultAnthropicReasoningEffort
                        )}
                        placeholder={"Select reasoning effort"}
                    />
                </Form.Item>
            );
        }

        if (type === AiExtractorType.GROK_API && (selectedSupportedModel?.supportedGrokReasoningEfforts?.length ?? 0) > 0) {
            return (
                <Form.Item name="grokReasoningEffort" label="Reasoning effort">
                    <Select
                        allowClear
                        options={buildOptionListWithDefault(
                            selectedSupportedModel?.supportedGrokReasoningEfforts || [],
                            selectedSupportedModel?.defaultGrokReasoningEffort
                        )}
                        placeholder={"Select reasoning effort"}
                    />
                </Form.Item>
            );
        }

        return null;
    }

    function renderWebSearchFields() {
        if (type === AiExtractorType.OLLAMA) {
            return null;
        }

        return (
            <AiExtractorWebSearchFields
                selectedModel={selectedModel}
                selectedWebSearchMode={selectedWebSearchMode}
                type={type}
                webSearchSupportedBySelectedModel={webSearchSupportedBySelectedModel}
            />
        );
    }

    function renderPromptFields() {
        const aiPromptOptions = buildAiPromptSelectOptions(getVisibleAiPromptsForCurrentScope(), isSystemAdmin);

        return <>
            <Form.Item name="systemPromptId" label="Main prompt">
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select main prompt"
                    options={aiPromptOptions}
                />
            </Form.Item>

            <Form.Item name="supplementaryPromptId" label="Additional instructions">
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select additional instructions"
                    options={aiPromptOptions}
                />
            </Form.Item>
        </>;
    }

    function getVisibleAiPromptsForCurrentScope() {
        const effectiveOwner = isSystemAdmin ? (owner ?? summarizer?.owner) : ResourceOwnerType.ORGANIZATION;
        const effectiveOrganizationId = isSystemAdmin
            ? (organizationId ?? summarizer?.organization?.id)
            : appContext.user.organization?.id;

        if (!effectiveOwner) {
            return availableAiPrompts;
        }

        if (effectiveOwner === ResourceOwnerType.SYSTEM) {
            return availableAiPrompts.filter(prompt => prompt.owner === ResourceOwnerType.SYSTEM);
        }

        return availableAiPrompts.filter(prompt =>
            prompt.owner === ResourceOwnerType.SYSTEM
            || (effectiveOrganizationId
                && prompt.owner === ResourceOwnerType.ORGANIZATION
                && prompt.organization?.id === effectiveOrganizationId)
        );
    }

    function renderFormButtons() {
        return (
            <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                <Button type="primary" htmlType="submit" loading={saving}>
                    Save
                </Button>
                <Button
                    style={{marginLeft: 8}}
                    onClick={onCancelEdit}>
                    Cancel
                </Button>
            </Form.Item>
        );
    }

    function renderDetails() {
        if (!summarizer) {
            return null;
        }

        const detailWebSearchMode = effectiveWebSearchMode(summarizer.type, summarizer.webSearchMode);
        const detailSupportedModels = getSupportedModelsForType(allSupportedModels, summarizer.type);
        const detailSupportedModel = detailSupportedModels.find(model => model.name === summarizer.model);
        const detailWebSearchSupported = isWebSearchSupportedForModel(summarizer.type, summarizer.model, detailSupportedModel);

        return (
            <div className={styles.details}>
                <div>
                    <Descriptions
                        column={1}
                        bordered={true}
                        size="small"
                        className={globalStyles.details}
                        title={"General"}
                    >
                        <Descriptions.Item label="Owner">
                            {summarizer.owner === ResourceOwnerType.SYSTEM
                                ? <i>System AI report summarizer</i>
                                : renderOrganizationLink(summarizer.organization)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Default">
                            {summarizer.defaultSummarizer ? "yes" : "no"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Name">{summarizer.name}</Descriptions.Item>
                        <Descriptions.Item label="Description">
                            {summarizer.description || <i>no description</i>}
                        </Descriptions.Item>
                    </Descriptions>

                    <Descriptions
                        column={1}
                        bordered={true}
                        size="small"
                        className={globalStyles.details}
                        title={"Model configuration"}
                    >
                        <Descriptions.Item label="Type">{formatAiExtractorType(summarizer.type)}</Descriptions.Item>
                        <Descriptions.Item label="Model">{summarizer.model}</Descriptions.Item>
                        {isSystemAdmin && summarizer.openAiServiceTier &&
                            <Descriptions.Item label="Service tier">
                                {String(summarizer.openAiServiceTier).toLowerCase()}
                            </Descriptions.Item>
                        }
                        {summarizer.aiProviderConnection &&
                            <Descriptions.Item label="Provider connection">
                                <Link to={`/definitions/ai-provider-connections/${summarizer.aiProviderConnection.id}`}>
                                    {summarizer.aiProviderConnection.name}
                                </Link>
                            </Descriptions.Item>
                        }
                        {isSystemAdmin &&
                            <Descriptions.Item label="Log AI provider calls">
                                {summarizer.aiProviderCommunicationLoggingEnabled ? "enabled" : "disabled"}
                            </Descriptions.Item>
                        }
                        {isSystemAdmin && (summarizer.type === AiExtractorType.OPENAI_API || summarizer.type === AiExtractorType.OPENAI_COMPATIBLE_API) &&
                            <Descriptions.Item label="Reasoning effort">
                                {summarizer.openAiReasoningEffort
                                    ? String(summarizer.openAiReasoningEffort).toLowerCase()
                                    : <i>none</i>}
                            </Descriptions.Item>
                        }
                        {isSystemAdmin && summarizer.type === AiExtractorType.ANTHROPIC_API && summarizer.anthropicReasoningEffort &&
                            <Descriptions.Item label="Reasoning effort">
                                {String(summarizer.anthropicReasoningEffort).toLowerCase()}
                            </Descriptions.Item>
                        }
                        {isSystemAdmin && summarizer.type === AiExtractorType.GROK_API && summarizer.grokReasoningEffort &&
                            <Descriptions.Item label="Reasoning effort">
                                {String(summarizer.grokReasoningEffort).toLowerCase()}
                            </Descriptions.Item>
                        }
                        {(detailWebSearchMode !== AiExtractorWebSearchMode.DISABLED || detailWebSearchSupported) &&
                            <Descriptions.Item label="Web search">
                                {formatAiExtractorWebSearchMode(detailWebSearchMode)}
                            </Descriptions.Item>
                        }
                        {detailWebSearchMode === AiExtractorWebSearchMode.EXTERNAL &&
                            <Descriptions.Item label="Web search backend">
                                {formatExternalWebSearchBackend(summarizer.externalWebSearchBackend)}
                            </Descriptions.Item>
                        }
                    </Descriptions>
                </div>

                {(isSystemAdmin || canReadAiPrompts || summarizer.systemPrompt || summarizer.supplementaryPrompt) && <div>
                    <Descriptions
                        column={1}
                        bordered={true}
                        size="small"
                        className={globalStyles.details}
                        title={"Prompting"}
                    >
                        <Descriptions.Item label="Main prompt">
                            {summarizer.systemPrompt
                                ? <Link to={`/definitions/ai-prompts/${summarizer.systemPrompt.id}`}>{summarizer.systemPrompt.name}</Link>
                                : <i>no main prompt</i>}
                        </Descriptions.Item>
                        <Descriptions.Item label="Additional instructions">
                            {summarizer.supplementaryPrompt
                                ? <Link to={`/definitions/ai-prompts/${summarizer.supplementaryPrompt.id}`}>{summarizer.supplementaryPrompt.name}</Link>
                                : <i>no additional instructions</i>}
                        </Descriptions.Item>
                    </Descriptions>
                </div>}
            </div>
        );
    }

    function reload() {
        if (!aiReportSummarizerId) {
            return;
        }

        return aiReportSummarizerService.get(aiReportSummarizerId)
            .then(value => {
                setSummarizer(value);
                form.setFieldsValue(toFormValues(value));
            });
    }

    function onFinish(values: any) {
        const payload: any = {
            ...values,
            webSearchMode: effectiveWebSearchMode(values.type, values.webSearchMode)
        };

        if (!isSystemAdmin) {
            payload.owner = ResourceOwnerType.ORGANIZATION;
            payload.organization = appContext.user.organization?.id;
            payload.aiProviderConnectionId = undefined;
            payload.openAiServiceTier = undefined;
            payload.openAiReasoningEffort = undefined;
            payload.anthropicReasoningEffort = undefined;
            payload.grokReasoningEffort = undefined;
        }

        if (payload.owner === ResourceOwnerType.SYSTEM) {
            payload.organization = undefined;
        }

        setSaving(true);

        const save = aiReportSummarizerId
            ? aiReportSummarizerService.update(aiReportSummarizerId, payload)
                .then(() => {
                    message.success("AI report summarizer updated.");

                    setEditMode(false);

                    return reload();
                })
            : aiReportSummarizerService.add(payload)
                .then(id => {
                    message.success("AI report summarizer added.");

                    navigate(`/definitions/ai-report-summarizers/${id}`);
                });

        save.finally(() => setSaving(false));
    }

    function onCancelEdit() {
        if (aiReportSummarizerId) {
            setEditMode(false);

            if (summarizer) {
                form.setFieldsValue(toFormValues(summarizer));
            }
        } else {
            navigate("/definitions/ai-report-summarizers");
        }
    }

    function onDelete() {
        if (!summarizer) {
            return;
        }

        aiReportSummarizerService.delete(summarizer)
            .then(() => {
                message.success("AI report summarizer deleted.");

                navigate("/definitions/ai-report-summarizers");
            });
    }
}

function toFormValues(value: AiReportSummarizer) {
    return {
        name: value.name,
        description: value.description,
        type: value.type,
        model: value.model,
        owner: value.owner,
        organization: value.organization?.id,
        webSearchMode: effectiveWebSearchMode(value.type, value.webSearchMode),
        externalWebSearchBackend: value.externalWebSearchBackend || ExternalWebSearchBackend.BRAVE,
        systemPromptId: value.systemPrompt?.id,
        supplementaryPromptId: value.supplementaryPrompt?.id,
        aiProviderConnectionId: value.aiProviderConnection?.id,
        openAiServiceTier: value.openAiServiceTier,
        openAiReasoningEffort: value.openAiReasoningEffort,
        anthropicReasoningEffort: value.anthropicReasoningEffort,
        grokReasoningEffort: value.grokReasoningEffort,
        aiProviderCommunicationLoggingEnabled: value.aiProviderCommunicationLoggingEnabled
    };
}

function effectiveWebSearchMode(type?: AiExtractorType, webSearchMode?: AiExtractorWebSearchMode) {
    return type === AiExtractorType.OLLAMA
        ? AiExtractorWebSearchMode.DISABLED
        : webSearchMode || AiExtractorWebSearchMode.DISABLED;
}

function buildOptionListWithDefault<T extends string>(values: T[], defaultValue?: T) {
    return values.map(value => ({
        value,
        label: defaultValue === value
            ? `${String(value).toLowerCase()} (default)`
            : String(value).toLowerCase()
    }));
}

export default AiReportSummarizerDetail;
